import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  InputBase,
  useMediaQuery,
  useTheme,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Paper,
  CircularProgress,
  ClickAwayListener,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  Settings,
  Logout,
  Circle as CircleIcon,
  DoneAll as DoneAllIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  DevicesOther as AssetIcon,
  Business as VendorIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'
import { useNotifications } from '@context/NotificationContext'
import { formatDistanceToNow } from 'date-fns'

const DRAWER_WIDTH = 260

const getNotificationIcon = (type) => {
  switch (type) {
    case 'work_order':
      return <AssignmentIcon fontSize="small" color="primary" />
    case 'pm_reminder':
      return <BuildIcon fontSize="small" color="warning" />
    case 'low_stock':
      return <InventoryIcon fontSize="small" color="error" />
    case 'calibration_due':
    case 'warranty_expiry':
      return <WarningIcon fontSize="small" color="warning" />
    default:
      return <NotificationsIcon fontSize="small" color="info" />
  }
}

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical':
      return 'error'
    case 'high':
      return 'warning'
    case 'medium':
      return 'info'
    default:
      return 'default'
  }
}

const Topbar = ({ onMenuClick, sidebarOpen = true }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, logout } = useAuth()
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const [anchorEl, setAnchorEl] = useState(null)
  const [notificationAnchor, setNotificationAnchor] = useState(null)

  // Global search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState({ assets: [], work_orders: [], inventory: [], vendors: [] })
  const [searchLoading, setSearchLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const searchTimeoutRef = useRef(null)
  const searchInputRef = useRef(null)

  // Debounced search effect
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults({ assets: [], work_orders: [], inventory: [], vendors: [] })
      setShowResults(false)
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true)
      setShowResults(true)
      try {
        const [assetsRes, workOrdersRes, inventoryRes, vendorsRes] = await Promise.all([
          api.get('/assets/assets/', { params: { search: searchQuery, page_size: 5 } }).catch(() => ({ data: { results: [] } })),
          api.get('/maintenance/work-orders/', { params: { search: searchQuery, page_size: 5 } }).catch(() => ({ data: { results: [] } })),
          api.get('/inventory/spare-parts/', { params: { search: searchQuery, page_size: 5 } }).catch(() => ({ data: { results: [] } })),
          api.get('/vendors/', { params: { search: searchQuery, page_size: 5 } }).catch(() => ({ data: { results: [] } })),
        ])

        setSearchResults({
          assets: assetsRes.data.results || assetsRes.data || [],
          work_orders: workOrdersRes.data.results || workOrdersRes.data || [],
          inventory: inventoryRes.data.results || inventoryRes.data || [],
          vendors: vendorsRes.data.results || vendorsRes.data || [],
        })
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        setSearchLoading(false)
      }
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchQuery])

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleSearchResultClick = (type, id) => {
    setShowResults(false)
    setSearchQuery('')
    switch (type) {
      case 'asset':
        navigate(`/assets/${id}`)
        break
      case 'work_order':
        navigate(`/work-orders/${id}`)
        break
      case 'inventory':
        navigate(`/inventory`)
        break
      case 'vendor':
        navigate(`/vendors`)
        break
      default:
        break
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setShowResults(false)
    setSearchResults({ assets: [], work_orders: [], inventory: [], vendors: [] })
  }

  const totalResults = searchResults.assets.length + searchResults.work_orders.length +
                       searchResults.inventory.length + searchResults.vendors.length

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget)
  }

  const handleNotificationClose = () => {
    setNotificationAnchor(null)
  }

  const handleNotificationItemClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id)
    }
    handleNotificationClose()

    // Navigate to notification link or derive route from notification type/metadata
    if (notification.link) {
      navigate(notification.link)
    } else if (notification.metadata?.object_id) {
      // Try to derive route from notification type and metadata
      const objectId = notification.metadata.object_id
      const type = notification.notification_type || notification.type
      switch (type) {
        case 'work_order':
          navigate(`/work-orders/${objectId}`)
          break
        case 'asset':
          navigate(`/assets/${objectId}`)
          break
        case 'pm_reminder':
          navigate(`/preventive-maintenance`)
          break
        case 'low_stock':
          navigate(`/inventory`)
          break
        default:
          navigate('/notifications')
      }
    } else {
      // Default: go to notifications page
      navigate('/notifications')
    }
  }

  const handleMarkAllRead = () => {
    markAllAsRead()
  }

  const handleLogout = () => {
    handleProfileMenuClose()
    logout()
  }

  const handleSettingsClick = () => {
    handleProfileMenuClose()
    navigate('/settings')
  }

  const getUserInitials = () => {
    if (!user) return 'U'
    const first = user.first_name?.[0] || user.username?.[0] || ''
    const last = user.last_name?.[0] || ''
    return (first + last).toUpperCase() || 'U'
  }

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return 'Just now'
    }
  }

  // Show recent notifications (max 5)
  const recentNotifications = notifications.slice(0, 5)

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: {
          xs: '100%',
          md: sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
        },
        ml: {
          xs: 0,
          md: sidebarOpen ? `${DRAWER_WIDTH}px` : 0,
        },
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={onMenuClick}
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Search */}
          <ClickAwayListener onClickAway={() => setShowResults(false)}>
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#f1f5f9',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  minWidth: { xs: 150, sm: 250, md: 300 },
                }}
              >
                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <InputBase
                  ref={searchInputRef}
                  placeholder="Search assets, work orders, inventory..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
                  sx={{
                    flex: 1,
                    '& input': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                {searchLoading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                {searchQuery && !searchLoading && (
                  <IconButton size="small" onClick={clearSearch} sx={{ p: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {/* Search Results Dropdown */}
              {showResults && (
                <Paper
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 1,
                    maxHeight: 400,
                    overflow: 'auto',
                    zIndex: 1300,
                    minWidth: 350,
                  }}
                >
                  {searchLoading ? (
                    <Box py={3} textAlign="center">
                      <CircularProgress size={24} />
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Searching...
                      </Typography>
                    </Box>
                  ) : totalResults === 0 ? (
                    <Box py={3} textAlign="center">
                      <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        No results found for "{searchQuery}"
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Assets Results */}
                      {searchResults.assets.length > 0 && (
                        <>
                          <Box px={2} py={1} bgcolor="grey.100">
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              ASSETS ({searchResults.assets.length})
                            </Typography>
                          </Box>
                          {searchResults.assets.map((asset) => (
                            <MenuItem
                              key={`asset-${asset.id}`}
                              onClick={() => handleSearchResultClick('asset', asset.id)}
                              sx={{ py: 1.5 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <AssetIcon fontSize="small" color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={asset.name}
                                secondary={`${asset.asset_tag || ''} • ${asset.location_name || 'No location'}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {/* Work Orders Results */}
                      {searchResults.work_orders.length > 0 && (
                        <>
                          <Box px={2} py={1} bgcolor="grey.100">
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              WORK ORDERS ({searchResults.work_orders.length})
                            </Typography>
                          </Box>
                          {searchResults.work_orders.map((wo) => (
                            <MenuItem
                              key={`wo-${wo.id}`}
                              onClick={() => handleSearchResultClick('work_order', wo.id)}
                              sx={{ py: 1.5 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <AssignmentIcon fontSize="small" color="warning" />
                              </ListItemIcon>
                              <ListItemText
                                primary={wo.title}
                                secondary={`#${wo.id} • ${wo.status}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {/* Stock Results */}
                      {searchResults.inventory.length > 0 && (
                        <>
                          <Box px={2} py={1} bgcolor="grey.100">
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              STOCK ({searchResults.inventory.length})
                            </Typography>
                          </Box>
                          {searchResults.inventory.map((item) => (
                            <MenuItem
                              key={`inv-${item.id}`}
                              onClick={() => handleSearchResultClick('inventory', item.id)}
                              sx={{ py: 1.5 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <InventoryIcon fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={item.name}
                                secondary={`${item.part_number || ''} • Qty: ${item.quantity_in_stock || 0}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {/* Vendors Results */}
                      {searchResults.vendors.length > 0 && (
                        <>
                          <Box px={2} py={1} bgcolor="grey.100">
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                              VENDORS ({searchResults.vendors.length})
                            </Typography>
                          </Box>
                          {searchResults.vendors.map((vendor) => (
                            <MenuItem
                              key={`vendor-${vendor.id}`}
                              onClick={() => handleSearchResultClick('vendor', vendor.id)}
                              sx={{ py: 1.5 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <VendorIcon fontSize="small" color="info" />
                              </ListItemIcon>
                              <ListItemText
                                primary={vendor.name}
                                secondary={vendor.contact_person || vendor.email || 'No contact'}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {/* Notifications */}
          <IconButton
            onClick={handleNotificationClick}
            sx={{ color: 'text.secondary' }}
          >
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Menu */}
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              ml: 1,
              p: 0.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
            }}
          >
            <Avatar
              src={user?.profile?.profile_picture_url || user?.profile?.profile_picture}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: '#1B5E7A',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
            {!isMobile && (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.primary"
                  lineHeight={1.2}
                >
                  {user?.first_name
                    ? `${user.first_name} ${user.last_name || ''}`
                    : user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          PaperProps={{
            sx: { minWidth: 200, mt: 1 },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box px={2} py={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user?.first_name
                ? `${user.first_name} ${user.last_name || ''}`
                : user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleSettingsClick}>
            <Settings fontSize="small" sx={{ mr: 1.5 }} />
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <Logout fontSize="small" sx={{ mr: 1.5 }} />
            Logout
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          PaperProps={{
            sx: { minWidth: 360, maxWidth: 400, maxHeight: 480, mt: 1 },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Notifications
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  size="small"
                  color="error"
                  sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllRead}
                sx={{ textTransform: 'none' }}
              >
                Mark all read
              </Button>
            )}
          </Box>
          <Divider />

          {recentNotifications.length === 0 ? (
            <Box py={4} textAlign="center">
              <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <>
              {recentNotifications.map((userNotification) => {
                // Handle both nested (from API) and flat notification structure
                const notif = userNotification.notification || userNotification
                const isRead = userNotification.is_read ?? notif.is_read ?? false
                return (
                  <MenuItem
                    key={userNotification.id}
                    onClick={() => handleNotificationItemClick({ ...notif, id: userNotification.id, is_read: isRead })}
                    sx={{
                      py: 1.5,
                      backgroundColor: isRead ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: isRead ? 'action.hover' : 'action.selected',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notif.type || notif.notification_type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            fontWeight={isRead ? 400 : 600}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                            }}
                          >
                            {notif.title}
                          </Typography>
                          {notif.priority && notif.priority !== 'low' && (
                            <Chip
                              label={notif.priority}
                              size="small"
                              color={getPriorityColor(notif.priority)}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: 240,
                              lineHeight: 1.4,
                            }}
                          >
                            {notif.message || 'No message'}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            {formatTime(userNotification.created_at || notif.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    {!isRead && (
                      <CircleIcon sx={{ fontSize: 8, color: 'primary.main', ml: 1 }} />
                    )}
                  </MenuItem>
                )
              })}
              <Divider />
              <MenuItem
                onClick={() => {
                  handleNotificationClose()
                  navigate('/notifications')
                }}
                sx={{ justifyContent: 'center', color: 'primary.main', py: 1.5 }}
              >
                View all notifications
              </MenuItem>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Topbar
